import { useEffect } from "react";

/**
 * Run `load` now and every `intervalMs` while the pane is VISIBLE; stop while it
 * is hidden; run it once the moment it is shown again.
 *
 * The Outlook pane stays mounted while the AM works in another window, and its
 * reads used to keep firing every 30 s regardless (plugin traffic review,
 * 2026-10-06). A hidden pane has nobody reading it.
 *
 * `load` receives `isCurrent()`: false once this cycle is torn down (unmount, a
 * new `load`, `enabled` flipping), so a late response never writes into a stale
 * or unmounted component. A new `load` identity — e.g. the mailbox scope
 * resolving — restarts the cycle with an immediate read.
 */
export function useVisiblePoll(
  load: (isCurrent: () => boolean) => void,
  intervalMs: number,
  enabled: boolean,
): void {
  useEffect(() => {
    if (!enabled) return;
    let alive = true;
    const isCurrent = () => alive;
    let timer: number | null = null;
    const hidden = () =>
      typeof document !== "undefined" && document.visibilityState === "hidden";
    const stop = () => {
      if (timer !== null) {
        window.clearInterval(timer);
        timer = null;
      }
    };
    const start = () => {
      stop();
      load(isCurrent);
      timer = window.setInterval(() => load(isCurrent), intervalMs);
    };
    const onVisibility = () => {
      if (hidden()) stop();
      else start();
    };
    if (!hidden()) start();
    document.addEventListener("visibilitychange", onVisibility);
    return () => {
      alive = false;
      stop();
      document.removeEventListener("visibilitychange", onVisibility);
    };
  }, [load, intervalMs, enabled]);
}

export default useVisiblePoll;
